import React, { useState, useRef } from 'react';
import { IoSendSharp } from 'react-icons/io5';
import { useTranslation } from '../localization/useAppTranslation';
import { logger } from '../utils/logger';

const moduleLogger = logger.getModule('MessageInput');

export interface MessageInputProps {
  value: string;
  onChange: (innerHtml: string, textContent: string) => void;
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  inputRef?: React.RefObject<any>;
  className?: string;
}

/**
 * Supports Enter to send, Shift+Enter for a new line, and an explicit send action.
 */
export const MessageInput = React.forwardRef<HTMLDivElement, MessageInputProps>(
  (
    {
      value,
      onChange,
      onSend,
      disabled = false,
      placeholder,
      inputRef,
      className = '',
    },
    ref
  ) => {
    const { t } = useTranslation('chat');
    const [isFocused, setIsFocused] = useState(false);
    const localInputRef = useRef<HTMLTextAreaElement>(null);
    const activeRef = inputRef || localInputRef;

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const textContent = e.target.value;
      onChange('', textContent);
    };

    const handleSend = () => {
      if (value.trim() && !disabled) {
        onSend(value);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Send on Enter (not mobile), but allow Shift+Enter for new lines
      if (e.key === 'Enter' && !e.shiftKey && !disabled) {
        e.preventDefault();
        handleSend();
      }
    };

    const inputContainerClass = [
      'message-input-container',
      isFocused && 'focused',
      disabled && 'disabled',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={inputContainerClass} data-tour="message-composer">
        <textarea
          ref={activeRef}
          className="message-input-field"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          placeholder={placeholder || t('composer.placeholder')}
          rows={1}
          aria-label={t('composer.inputAriaLabel')}
        />
        <button
          className="message-send-button"
          onClick={handleSend}
          disabled={disabled || !value.trim()}
          aria-label={t('composer.sendMessage')}
          title={t('composer.sendMessage')}
          type="button"
        >
          <IoSendSharp aria-hidden="true" />
        </button>
      </div>
    );
  }
);

MessageInput.displayName = 'MessageInput';
